import * as Sentry from "@sentry/react";

export interface SentryConfig {
  dsn?: string;
  environment: string;
  release: string;
}

/**
 * Sentry is errors only (STACK.md, LOGGING.md): no tracing, no replay, no Sentry Logs, no
 * release-health session tracking — logs live in platform stdout. `sendDefaultPii` stays off;
 * `beforeSend` scrubs anything that isn't an ID (the PII law, .claude/rules/secrets.md) before
 * an event ever leaves the browser. Absent DSN = disabled (dev without the secret); the env
 * module requires it for prod builds.
 *
 * Session tracking: `autoSessionTracking` was removed in the JS SDK's v9 (verified against
 * Sentry's v8->v9 migration guide, docs.sentry.io, 2026-08 — not assumed from memory, since the
 * mechanism changed across majors). The current, integration-based equivalent is excluding
 * `browserSessionIntegration` (registered under the name `"BrowserSession"`) from the default
 * integration set; there is no boolean option anymore.
 */
export function initSentry(config: SentryConfig): void {
  Sentry.init({
    ...(config.dsn ? { dsn: config.dsn } : {}),
    release: config.release,
    enabled: config.dsn !== undefined,
    environment: config.environment,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    integrations: (defaultIntegrations) =>
      defaultIntegrations.filter((integration) => integration.name !== "BrowserSession"),
    beforeSend: scrubEvent,
  });
}

// Types derived structurally from Sentry.ErrorEvent's own declared fields rather than imported
// by name — the scrub always matches whatever shape the installed SDK actually declares, even
// if a future version renames the exported type.
type EventRequest = NonNullable<Sentry.ErrorEvent["request"]>;
type EventBreadcrumb = NonNullable<Sentry.ErrorEvent["breadcrumbs"]>[number];
type EventException = NonNullable<NonNullable<Sentry.ErrorEvent["exception"]>["values"]>[number];
type EventStackFrame = NonNullable<NonNullable<EventException["stacktrace"]>["frames"]>[number];
type EventContexts = NonNullable<Sentry.ErrorEvent["contexts"]>;

// Email and bearer/JWT-shaped tokens have a detectable lexical pattern; a free-form "name" does
// not. Name risk is handled structurally below — fields that could carry one (request bodies,
// headers, cookies, extra, breadcrumb messages, non-allowlisted contexts) are dropped outright,
// never content-scanned — so this pattern list only needs to cover what's shape-detectable.
const JWT_PATTERN = /\bey[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g;
const BEARER_PATTERN = /\bBearer\s+\S+/gi;
const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

/** Applied to the handful of free-text fields kept for diagnostic value (exception messages,
 * URLs) — never a substitute for the structural drops below. */
function redact(text: string): string {
  return text
    .replace(JWT_PATTERN, "[redacted-token]")
    .replace(BEARER_PATTERN, "Bearer [redacted-token]")
    .replace(EMAIL_PATTERN, "[redacted-email]");
}

function stripQueryString(url: string): string {
  return url.split("?")[0] ?? url;
}

/** Allowlist: `url` (query-stripped, content-redacted) and `method` only. `data`, `query_string`,
 * `cookies`, `env`, and `headers` (Authorization included) are never copied onto the result —
 * dropped by construction, not by exclusion. */
function scrubRequest(request: EventRequest): EventRequest {
  const safe: EventRequest = {};
  if (request.url) {
    safe.url = redact(stripQueryString(request.url));
  }
  if (request.method) {
    safe.method = request.method;
  }
  return safe;
}

/** Allowlist: `category`/`type`/`level`/`timestamp` (closed vocabularies, never free text) plus
 * a content-redacted `url`/`method`/`status_code` lifted out of `data` when present. `message`
 * (free text — e.g. a console breadcrumb can carry anything) and every other `data` key
 * (request/response bodies included) are dropped. */
function scrubBreadcrumb(breadcrumb: EventBreadcrumb): EventBreadcrumb {
  const safe: EventBreadcrumb = {};
  if (breadcrumb.category) {
    safe.category = breadcrumb.category;
  }
  if (breadcrumb.type) {
    safe.type = breadcrumb.type;
  }
  if (breadcrumb.level) {
    safe.level = breadcrumb.level;
  }
  if (breadcrumb.timestamp !== undefined) {
    safe.timestamp = breadcrumb.timestamp;
  }

  const url = breadcrumb.data?.["url"];
  const method = breadcrumb.data?.["method"];
  const statusCode = breadcrumb.data?.["status_code"];
  const data: Record<string, unknown> = {};
  if (typeof url === "string") {
    data["url"] = redact(stripQueryString(url));
  }
  if (typeof method === "string") {
    data["method"] = method;
  }
  if (typeof statusCode === "number") {
    data["status_code"] = statusCode;
  }
  if (Object.keys(data).length > 0) {
    safe.data = data;
  }
  return safe;
}

/** Allowlist: source-location metadata only. `vars` (local variable snapshots, when an
 * integration captures them) and the `context_line`/`pre_context`/`post_context` source-code
 * lines can carry arbitrary runtime data — dropped. */
function scrubFrame(frame: EventStackFrame): EventStackFrame {
  const safe: EventStackFrame = {};
  if (frame.filename) {
    safe.filename = frame.filename;
  }
  if (frame.function) {
    safe.function = frame.function;
  }
  if (frame.module) {
    safe.module = frame.module;
  }
  if (frame.lineno !== undefined) {
    safe.lineno = frame.lineno;
  }
  if (frame.colno !== undefined) {
    safe.colno = frame.colno;
  }
  if (frame.in_app !== undefined) {
    safe.in_app = frame.in_app;
  }
  return safe;
}

/** `value` (the error message) is kept and content-redacted, not dropped — the app's own
 * register-correct error messages (CRAFT) are the single most useful diagnostic field, and
 * dropping every exception message outright would defeat the point of reporting at all. Every
 * stack-adjacent surface that could carry runtime data instead — `mechanism.data`, frame `vars`,
 * frame context lines — is allowlisted away by `scrubFrame` and the `mechanism` handling below. */
function scrubException(exception: EventException): EventException {
  const safe: EventException = {};
  if (exception.type) {
    safe.type = exception.type;
  }
  if (exception.value) {
    safe.value = redact(exception.value);
  }
  if (exception.module) {
    safe.module = exception.module;
  }
  if (exception.mechanism) {
    safe.mechanism = {
      type: exception.mechanism.type,
      ...(exception.mechanism.handled !== undefined
        ? { handled: exception.mechanism.handled }
        : {}),
    };
  }
  if (exception.stacktrace?.frames) {
    safe.stacktrace = { frames: exception.stacktrace.frames.map(scrubFrame) };
  }
  return safe;
}

/** Only Sentry's own technical, non-PII contexts survive — app/browser/device/os/culture carry
 * things like version numbers, screen size, and locale, never user content. Everything else,
 * including `state` (a literal application-state dump) and `response` (can carry cookies and
 * headers), is dropped. */
function scrubContexts(contexts: EventContexts): EventContexts {
  const safe: EventContexts = {};
  if (contexts["app"]) {
    safe["app"] = contexts["app"];
  }
  if (contexts["browser"]) {
    safe["browser"] = contexts["browser"];
  }
  if (contexts.device) {
    safe.device = contexts.device;
  }
  if (contexts.os) {
    safe.os = contexts.os;
  }
  if (contexts.culture) {
    safe.culture = contexts.culture;
  }
  return safe;
}

/**
 * The PII law (allowlist-shaped, fail closed — CLAUDE.md, .claude/rules/secrets.md): every event
 * surface that can carry non-ID data is rebuilt from a known-safe field list, never stripped of
 * known-bad ones. A field Sentry adds tomorrow that this function doesn't know about is dropped
 * by default, not leaked by default. `event.tags` is deliberately never touched here: it carries
 * only what `captureApiFailure` puts there (code, requestId) — our own controlled strings, never
 * user input — so the taxonomy's requestId tag always survives every scrub intact.
 */
export function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.user) {
    if (event.user.id) {
      event.user = { id: event.user.id };
    } else {
      delete event.user;
    }
  }

  if (event.request) {
    event.request = scrubRequest(event.request);
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  }

  if (event.exception?.values) {
    event.exception = { values: event.exception.values.map(scrubException) };
  }

  if (event.contexts) {
    event.contexts = scrubContexts(event.contexts);
  }

  // A free-form debugging bag we never populate deliberately — dropped outright rather than
  // content-scanned (fail closed; not in the allowlist above).
  delete event.extra;
  // Only populated via Sentry.captureMessage, which this app never calls — dropped on the same
  // principle: not explicitly allowlisted, not kept.
  delete event.logentry;

  if (event.message) {
    event.message = redact(event.message);
  }

  return event;
}

/** The client failure taxonomy's one Sentry event (TASK.md), fired only from
 * src/lib/api/failure-handler.ts — never at the call site, never per-retry. */
export function captureApiFailure(error: unknown, tags: Record<string, string>): void {
  Sentry.captureException(error, { tags });
}
